import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import client, { downloadCsv } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, Download, Printer, FileSpreadsheet, FileText, Truck, Users2, Link2, Copy, Loader2, Package, Ban } from 'lucide-react';

export default function AuditReady() {
  const [data, setData] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  useEffect(() => { client.get('/audit/overview').then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  const dl = async (path, name) => { try { await downloadCsv(path, name); toast.success('Export started'); } catch { toast.error('Export failed'); } };
  if (!data) return <AppShell title="Audit-Ready"><Skeleton className="h-64" /></AppShell>;
  const T = data.totals;
  return (
    <AppShell title="Audit-Ready">
      <div className="mb-5 flex flex-col gap-3 rounded-xl border bg-[hsl(var(--primary))]/5 p-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3"><ShieldCheck className="h-8 w-8 text-[hsl(var(--primary))]" /><div><div className="font-display text-lg font-semibold">Inspector Mode — {data.company}</div><div className="text-sm text-muted-foreground">Read-only compliance snapshot for DOT/FMCSA auditors · Generated {new Date(data.generated_at).toLocaleString()}</div></div></div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => dl('/export/drivers.xlsx', 'hibret_drivers.xlsx')} data-testid="audit-export-drivers"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Drivers (Excel)</Button>
          <Button variant="outline" onClick={() => dl('/export/fleet.xlsx', 'hibret_fleet.xlsx')} data-testid="audit-export-fleet"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Fleet (Excel)</Button>
          <Button variant="outline" onClick={() => dl('/export/documents.pdf', 'hibret_documents.pdf')} data-testid="audit-export-documents"><FileText className="mr-1.5 h-4 w-4" />Documents (PDF)</Button>
          <Button variant="outline" onClick={() => dl('/export/violations.pdf', 'hibret_violations.pdf')} data-testid="audit-export-violations"><FileText className="mr-1.5 h-4 w-4" />Violations (PDF)</Button>
          <Button onClick={() => dl('/audit/packet.pdf', 'hibret_audit_packet.pdf')} data-testid="audit-packet-button"><Package className="mr-1.5 h-4 w-4" />Audit Packet (PDF)</Button>
          <Button variant="outline" onClick={() => setLinkOpen(true)} data-testid="audit-guest-link-button"><Link2 className="mr-1.5 h-4 w-4" />Guest Link</Button>
        </div>
      </div>

      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[{ l: 'ISS Score', v: data.iss_score, i: ShieldCheck }, { l: 'Active Drivers', v: T.active_drivers, i: Users2 }, { l: 'Vehicles', v: T.vehicles, i: Truck }, { l: 'Documents', v: T.documents, i: FileText }].map((m) => { const Ic = m.i; return (
          <Card key={m.l} className="flex items-center gap-3 p-4"><Ic className="h-6 w-6 text-[hsl(var(--primary))]" /><div><div className="text-xs uppercase tracking-wide text-muted-foreground">{m.l}</div><div className="font-display text-2xl font-bold">{m.v}</div></div></Card>
        ); })}
      </div>

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        {[['Compliant', data.compliance.compliant, 'compliant'], ['Warning', data.compliance.warning, 'warning'], ['Non-Compliant', data.compliance.non_compliant, 'non_compliant']].map(([l, v, s]) => (
          <Card key={l} className="flex items-center justify-between p-4"><StatusBadge status={s} label={l} /><span className="font-display text-2xl font-bold">{v}</span></Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="border-b p-4 font-display font-semibold">Driver Qualification Summary</div>
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Driver</TableHead><TableHead>Compliance</TableHead><TableHead>Missing</TableHead><TableHead>Expired</TableHead></TableRow></TableHeader>
          <TableBody>{data.drivers.map((d) => (
            <TableRow key={d.driver_id}><TableCell className="font-medium">{d.name}</TableCell><TableCell><StatusBadge status={d.status} /></TableCell><TableCell className="text-sm text-muted-foreground">{d.missing.join(', ') || '—'}</TableCell><TableCell className="text-sm text-[hsl(var(--danger))]">{d.expired.join(', ') || '—'}</TableCell></TableRow>
          ))}</TableBody>
        </Table></div>
      </Card>
      <Card className="mt-5 overflow-hidden" data-testid="guest-links-manager">
        <div className="flex items-center justify-between border-b p-4">
          <div className="font-display font-semibold">Auditor Guest Links</div>
          <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)} data-testid="guest-link-new-button"><Link2 className="mr-1.5 h-4 w-4" />New Link</Button>
        </div>
        <GuestLinksTable reloadKey={linkOpen} />
      </Card>
      <GuestLinkDialog open={linkOpen} onOpenChange={setLinkOpen} />
    </AppShell>
  );
}

function GuestLinksTable({ reloadKey }) {
  const [links, setLinks] = useState(null);
  const load = () => client.get('/audit/guest-links').then((r) => setLinks(r.data)).catch(() => setLinks([]));
  useEffect(() => { load(); }, [reloadKey]);
  const revoke = async (token) => {
    try { await client.delete(`/audit/guest-links/${token}`); toast.success('Guest link revoked'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const copyLink = (token) => { navigator.clipboard.writeText(`${window.location.origin}/audit-view/${token}`); toast.success('Link copied'); };
  if (links === null) return <div className="space-y-2 p-4">{[1,2].map(i => <Skeleton key={i} className="h-10" />)}</div>;
  if (!links.length) return <div className="p-8 text-center text-sm text-muted-foreground">No guest links created yet.</div>;
  return (
    <div className="overflow-x-auto"><Table>
      <TableHeader><TableRow><TableHead>Auditor</TableHead><TableHead>Created</TableHead><TableHead>Expires</TableHead><TableHead>State</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
      <TableBody>{links.map((l) => (
        <TableRow key={l.token} data-testid={`guest-link-row-${l.token}`}>
          <TableCell className="text-sm">{l.email || 'Shared link'}</TableCell>
          <TableCell className="text-sm">{l.created_at ? new Date(l.created_at).toLocaleDateString() : '-'}</TableCell>
          <TableCell className="text-sm">{l.expires_at ? new Date(l.expires_at).toLocaleDateString() : '-'}</TableCell>
          <TableCell><StatusBadge status={l.state === 'active' ? 'active' : l.state === 'expired' ? 'warning' : 'terminated'} label={l.state} /></TableCell>
          <TableCell className="text-right">
            <div className="flex items-center justify-end gap-1">
              {l.state === 'active' && <Button variant="ghost" size="sm" onClick={() => copyLink(l.token)} data-testid={`guest-copy-${l.token}`}><Copy className="mr-1 h-3.5 w-3.5" />Copy</Button>}
              {l.state === 'active' && <Button variant="ghost" size="sm" className="text-[hsl(var(--danger))]" onClick={() => revoke(l.token)} data-testid={`guest-revoke-${l.token}`}><Ban className="mr-1 h-3.5 w-3.5" />Revoke</Button>}
            </div>
          </TableCell>
        </TableRow>
      ))}</TableBody>
    </Table></div>
  );
}

function GuestLinkDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const generate = async () => {
    setLoading(true);
    try {
      const r = await client.post('/audit/guest-link', { days: Number(days), email });
      const url = `${window.location.origin}${r.data.link_path}`;
      setResult({ url, emailed: r.data.emailed, expires: r.data.expires_at });
      toast.success('Guest link generated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setResult(null); }}>
      <DialogContent data-testid="guest-link-dialog"><DialogHeader><DialogTitle>Auditor Guest Link</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Time-limited read-only audit link (expires {new Date(result.expires).toLocaleDateString()}). {result.emailed ? 'Emailed to the auditor.' : 'Email not sent — share the link manually.'}</p>
            <div className="flex items-center gap-2"><Input value={result.url} readOnly className="text-xs" data-testid="guest-link-value" /><Button size="icon" onClick={() => { navigator.clipboard.writeText(result.url); toast.success('Copied'); }}><Copy className="h-4 w-4" /></Button></div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Generate a read-only link for a DOT/FMCSA inspector — no account required.</p>
            <div><Label>Auditor Email (optional — emails the link)</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="inspector@dot.gov" className="mt-1" data-testid="guest-link-email" /></div>
            <div><Label>Valid for (days)</Label><Input type="number" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1" /></div>
          </div>
        )}
        <DialogFooter>{result ? <Button onClick={() => onOpenChange(false)}>Done</Button> : (<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={generate} disabled={loading} data-testid="guest-link-generate">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Generate</Button></>)}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

